import { HighlightTrans } from '@kc-console/common-ui';
import { EXTERNAL_LINK, isPublic } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { LogoKakaoCloudRegular } from '@kep/kcds-icons';

import HelpdeskButton from '../components/HelpdeskButton';
import InqueryButton from '../components/InqueryButton';
import PreviousPageButton from '../components/PreviousPageButton';
import FullWidthErrorTemplate from '../FullWidthErrorTemplate';

import type { ReactNode } from 'react';
import type { BaseErrorPageProps } from '../types';

export interface ActionTemporaryErrorProps extends BaseErrorPageProps {
  /**
   * 하단 액션 버튼들 재정의
   */
  actions?: ReactNode;
}

/**
 * 일시적인 오류 페이지 컴포넌트 (액션 버튼 포함)
 *
 * @description
 * FullWidthErrorTemplate을 사용하여 일시적인 시스템 오류 안내 페이지를 렌더링합니다.
 * "이전 페이지"와 "헬프데스크" 버튼이 표시됩니다.
 */
export default function ActionTemporaryError({
  actions,
  description,
  templateProps,
}: ActionTemporaryErrorProps): ReactNode {
  const { t } = useTranslation();

  const defaultDescription = (
    <>
      {t('ui:error_page.system_error.message')}
      {'\n'}
      {isPublic() ? (
        <HighlightTrans
          i18nKey="ui:error_page.system_error.issue_persists.description"
          componentKey="highlight"
          variant="label/semibold/lg"
        />
      ) : (
        <HighlightTrans
          i18nKey="ui:error_page.issue_persists.email_contact"
          componentKey="highlight"
          variant="label/semibold/lg"
          values={{
            contact_mail: EXTERNAL_LINK.GOV_HELPDESK_MAIL,
          }}
        />
      )}
    </>
  );

  const defaultActions = (
    <>
      <PreviousPageButton fullReloadOnRoute />
      {isPublic() ? <HelpdeskButton fullReloadOnRoute /> : <InqueryButton />}
    </>
  );

  return (
    <FullWidthErrorTemplate
      {...templateProps}
      logo={<LogoKakaoCloudRegular />}
      title={t('ui:error_page.temporary_error')}
      description={description ?? defaultDescription}
      actions={actions ?? defaultActions}
      containerWidth="484px"
    />
  );
}
